/* eslint-disable lingui/no-unlocalized-strings -- Native DEXPI class and relationship identifiers. */
import {
  nozzleClasses,
  pipeClasses,
  twoPortComponentClasses,
} from './dexpiNode'
import type { FlowDirection, FlowStep } from './flowTypes'
import type { PnidGraph } from './graphIndex'
import type { GraphNode } from './graphTypes'
import {
  isPipeOffPageConnector,
  resolveOffPageConnectorReference,
} from './offPageConnectorGraph'

export function isNodeOnPage(node: GraphNode, pageId: string): boolean {
  return (
    node.tags['system-document-page'] === pageId || node.overlays.length > 0
  )
}

function isNodeOnOtherPage(node: GraphNode, pageId: string): boolean {
  return (
    Boolean(node.tags['system-document-page']) && !isNodeOnPage(node, pageId)
  )
}

export function isLocalPipingNode(
  node: GraphNode | undefined,
  pageId: string
): node is GraphNode {
  return (
    node?.dexpi.dexpi_class === 'Plant.Piping.PipingNode' &&
    !isNodeOnOtherPage(node, pageId)
  )
}

function isOwnedPort(node: GraphNode | undefined, pageId: string): boolean {
  return (
    isLocalPipingNode(node, pageId) &&
    node.getIncomingDexpiEdges('Nodes').length === 1
  )
}

export function isLocalPipe(node: GraphNode, pageId: string): boolean {
  return pipeClasses.has(node.dexpi.dexpi_class) && isNodeOnPage(node, pageId)
}

export function pipeEnds(
  pipe: GraphNode,
  pageId: string
): [GraphNode, GraphNode] | undefined {
  const source = pipe.getUniqueTargetNode('SourceNode')
  const target = pipe.getUniqueTargetNode('TargetNode')
  return isLocalPipe(pipe, pageId) &&
    isLocalPipingNode(source, pageId) &&
    isLocalPipingNode(target, pageId)
    ? [source, target]
    : undefined
}

export function pipeDirection(pipe: GraphNode): FlowDirection {
  const segment = pipe.getUniqueSourceNode('Connections')
  if (segment?.dexpi.dexpi_class !== 'Plant.Piping.PipingNetworkSegment')
    return 'unknown'
  switch (segment.dexpi.attributes.FlowDirection) {
    case 'SingleFlowPipingNetworkSegment':
      return 'forward'
    case 'DualFlowPipingNetworkSegment':
      return 'both'
    default:
      return 'unknown'
  }
}

function componentPorts(
  component: GraphNode,
  pageId: string
): { ports: GraphNode[]; certain: boolean } | undefined {
  const dexpiClass = component.dexpi.dexpi_class
  const ports = component.getTargetNodes('Nodes')
  if (
    isNodeOnOtherPage(component, pageId) ||
    isPipeOffPageConnector(component) ||
    ports.length !== new Set(ports).size ||
    !ports.every((port) => isOwnedPort(port, pageId))
  )
    return undefined
  return {
    ports,
    certain:
      (twoPortComponentClasses.has(dexpiClass) && ports.length === 2) ||
      (dexpiClass === 'Plant.Piping.PipeTee' && ports.length === 3),
  }
}

function nozzleChamber(
  nozzle: GraphNode,
  pageId: string
): GraphNode | undefined {
  const chamber = nozzle.getUniqueTargetNode('Chamber')
  return chamber?.dexpi.dexpi_class === 'Plant.ProcessEquipment.Chamber' &&
    !isNodeOnOtherPage(chamber, pageId)
    ? chamber
    : undefined
}

function chamberEnds(chamber: GraphNode, pageId: string): GraphNode[] {
  return chamber
    .getIncomingDexpiEdges('Chamber')
    .flatMap(({ sourceNode: nozzle }) => {
      const end = nozzle.getUniqueTargetNode('Nodes')
      return nozzleClasses.has(nozzle.dexpi.dexpi_class) &&
        nozzle.getUniqueTargetNode('Chamber') === chamber &&
        isNodeOnPage(nozzle, pageId) &&
        end &&
        isOwnedPort(end, pageId)
        ? [end]
        : []
    })
}

export function flowNeighbors(
  pipingNode: GraphNode,
  pageId: string
): FlowStep[] {
  return pipingNode
    .getIncomingDexpiEdges()
    .flatMap(({ sourceNode: via, data }): FlowStep[] => {
      const field = data.dexpi_relationship_field
      if (field === 'SourceNode' || field === 'TargetNode') {
        const to = pipeEnds(via, pageId)?.find((end) => end !== pipingNode)
        return to ? [{ via, to, certain: true }] : []
      }
      if (field !== 'Nodes') return []
      const chamber = nozzleClasses.has(via.dexpi.dexpi_class)
        ? nozzleChamber(via, pageId)
        : undefined
      if (chamber) {
        const ends = chamberEnds(chamber, pageId)
        return ends.includes(pipingNode)
          ? ends
              .filter((end) => end !== pipingNode)
              .map((to) => ({ via: chamber, to, certain: true }))
          : []
      }
      const component = componentPorts(via, pageId)
      return component
        ? component.ports
            .filter((port) => port !== pipingNode)
            .map((to) => ({ via, to, certain: component.certain }))
        : []
    })
}

function isPageBoundaryConnector(
  connector: GraphNode,
  pageId: string
): boolean {
  const ports = connector.getTargetNodes('Nodes')
  return (
    ports.length === 1 &&
    isOwnedPort(ports[0], pageId) &&
    isNodeOnPage(connector, pageId)
  )
}

export function findBoundaries(graph: PnidGraph): Set<GraphNode> {
  const boundaries = new Set<GraphNode>()
  for (const node of graph.getNodes()) {
    if (isPipeOffPageConnector(node)) {
      if (
        isPageBoundaryConnector(node, graph.pageId) &&
        resolveOffPageConnectorReference(node)
      )
        boundaries.add(node.getTargetNodes('Nodes')[0])
    } else if (
      pipeClasses.has(node.dexpi.dexpi_class) &&
      isNodeOnOtherPage(node, graph.pageId)
    )
      for (const end of [
        node.getUniqueTargetNode('SourceNode'),
        node.getUniqueTargetNode('TargetNode'),
      ])
        if (isLocalPipingNode(end, graph.pageId)) boundaries.add(end)
  }
  return boundaries
}
